// Admin-only promo manager, reachable from the employee panel (behind the
// employee PIN — no public Django admin). Lists promos and lets an admin add,
// edit, and delete them. Each promo wires a quote template + a discount to a
// code that any rep can then pick from their Promo dropdown.

import { useEffect, useState } from "react";

import { api, ApiError } from "../api";
import type {
  DiscountScope,
  DiscountType,
  PromoFull,
  PromoSave,
  QuoteTemplate,
} from "../api";
import { useConfigurator } from "../store";
import { FormModal, Field, inputClass } from "./FormModal";

type Draft = PromoSave;

const EMPTY: Draft = {
  code: "",
  label: "",
  template_id: "",
  preloved_template_id: "",
  preloved_dated_template_id: "",
  preloved_graded_template_id: "",
  discount_type: "",
  discount_value: 0,
  discount_tiers: "",
  discount_scope: "order",
  active: true,
  auto_apply: false,
  starts_on: "",
  ends_on: "",
  trigger_kinds: [],
  trigger_families: [],
  trigger_product_ids: "",
  nudge_message: "",
  applied_message: "",
};

const KIND_OPTIONS: { value: string; label: string }[] = [
  { value: "machine", label: "New machine" },
  { value: "retrofit", label: "Retrofit" },
  { value: "upgrade", label: "Upgrade" },
  { value: "preloved_dated", label: "Pre-loved — dated" },
  { value: "preloved_graded", label: "Pre-loved — graded A/B/C" },
];
const FAMILY_OPTIONS = ["ascend", "statler", "vision"];

function toggleIn(arr: string[], value: string): string[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

/** Migrate a legacy generic "preloved" trigger to the two sub-kinds so editing
 *  an older promo shows both dated + graded checked. */
function normalizeKinds(kinds: string[]): string[] {
  if (!kinds.includes("preloved")) return kinds;
  const out = kinds.filter((k) => k !== "preloved");
  if (!out.includes("preloved_dated")) out.push("preloved_dated");
  if (!out.includes("preloved_graded")) out.push("preloved_graded");
  return out;
}

export function ManagePromosModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { state } = useConfigurator();
  const token = state.employee.token;

  const [promos, setPromos] = useState<PromoFull[]>([]);
  const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function refresh(tok: string) {
    setLoading(true);
    Promise.all([api.employeePromosManage(tok), api.employeeTemplates(tok)])
      .then(([p, t]) => {
        setPromos(p);
        setTemplates(t);
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "Couldn't load promos."),
      )
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (open && token) {
      setDraft(EMPTY);
      setError(null);
      refresh(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!token || saving) return;
    if (!draft.code.trim()) {
      setError("Code is required.");
      return;
    }
    if (
      draft.auto_apply &&
      !draft.trigger_kinds.length &&
      !draft.trigger_families.length &&
      !draft.trigger_product_ids.trim()
    ) {
      setError("Auto-apply needs at least one trigger (kind, family, or product ID).");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.employeePromoSave(token, {
        ...draft,
        code: draft.code.trim(),
        label: draft.label.trim(),
        discount_value: draft.discount_value || 0,
      });
      setDraft(EMPTY);
      refresh(token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save promo.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: number) {
    if (!token) return;
    setError(null);
    try {
      await api.employeePromoDelete(token, id);
      if (draft.id === id) setDraft(EMPTY);
      refresh(token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete promo.");
    }
  }

  const editing = draft.id != null;

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title="Manage promos"
      subtitle="Wire a template + discount to a code reps pick, or auto-apply it to matching carts with a banner."
    >
      {/* Existing promos */}
      {loading ? (
        <p className="mb-4 font-sans text-[14px] text-steel">Loading…</p>
      ) : promos.length === 0 ? (
        <p className="mb-4 font-sans text-[14px] text-steel">No promos yet.</p>
      ) : (
        <ul className="mb-5 divide-y divide-line border border-line">
          {promos.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <span className="font-sans text-[14px] font-semibold text-ink">
                  {p.code}
                </span>{" "}
                <span className="font-sans text-[13px] text-steel">
                  {describe(p)}
                  {!p.active && " · inactive"}
                </span>
              </div>
              <div className="flex flex-none gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setDraft({
                      id: p.id,
                      code: p.code,
                      label: p.label,
                      template_id: p.template_id,
                      preloved_template_id: p.preloved_template_id,
                      preloved_dated_template_id: p.preloved_dated_template_id,
                      preloved_graded_template_id: p.preloved_graded_template_id,
                      discount_type: p.discount_type,
                      discount_value: p.discount_value,
                      discount_tiers: p.discount_tiers,
                      discount_scope: p.discount_scope,
                      active: p.active,
                      auto_apply: p.auto_apply,
                      starts_on: p.starts_on,
                      ends_on: p.ends_on,
                      trigger_kinds: normalizeKinds(p.trigger_kinds),
                      trigger_families: p.trigger_families,
                      trigger_product_ids: p.trigger_product_ids,
                      nudge_message: p.nudge_message,
                      applied_message: p.applied_message,
                    })
                  }
                  className="font-sans text-[13px] font-semibold text-ink hover:underline"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  className="font-sans text-[13px] font-semibold text-red-700 hover:underline"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add / edit form */}
      <form onSubmit={save} className="border-t border-line pt-4">
        <p className="mb-3 font-display text-[13px] font-extrabold uppercase tracking-tag text-graphite">
          {editing ? `Edit ${draft.code}` : "New promo"}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" required>
            <input
              value={draft.code}
              onChange={(e) => setDraft({ ...draft, code: e.target.value })}
              placeholder="SPRING10"
              className={inputClass}
            />
          </Field>
          <Field label="Label">
            <input
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="Spring Sale"
              className={inputClass}
            />
          </Field>
        </div>

        <Field label="Quote template — new machines">
          <select
            value={draft.template_id}
            onChange={(e) => setDraft({ ...draft, template_id: e.target.value })}
            className={inputClass}
          >
            <option value="">Default template</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Quote template — pre-loved (default)">
          <select
            value={draft.preloved_template_id}
            onChange={(e) =>
              setDraft({ ...draft, preloved_template_id: e.target.value })
            }
            className={inputClass}
          >
            <option value="">Use the new-machine template above</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Quote template — pre-loved, dated (optional)">
          <select
            value={draft.preloved_dated_template_id}
            onChange={(e) =>
              setDraft({ ...draft, preloved_dated_template_id: e.target.value })
            }
            className={inputClass}
          >
            <option value="">Use the pre-loved default above</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Quote template — pre-loved, graded A/B/C (optional)">
          <select
            value={draft.preloved_graded_template_id}
            onChange={(e) =>
              setDraft({ ...draft, preloved_graded_template_id: e.target.value })
            }
            className={inputClass}
          >
            <option value="">Use the pre-loved default above</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Discount">
          <div className="flex items-center gap-2">
            <select
              value={draft.discount_type}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  discount_type: e.target.value as "" | DiscountType,
                })
              }
              className="border border-line bg-white px-3 py-2 font-sans text-[15px] text-ink focus:border-ink focus:outline-none"
            >
              <option value="">None</option>
              <option value="percent">% off</option>
              <option value="fixed">$ off</option>
              <option value="tiered">$ off by price tier</option>
            </select>
            {(draft.discount_type === "percent" ||
              draft.discount_type === "fixed") && (
              <div className="flex items-center gap-1">
                {draft.discount_type === "fixed" && (
                  <span className="font-sans text-[15px] text-ink">$</span>
                )}
                <input
                  type="number"
                  min={0}
                  step="any"
                  value={draft.discount_value || ""}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      discount_value: Number(e.target.value || 0),
                    })
                  }
                  className="w-28 border border-line bg-white px-3 py-2 font-sans text-[15px] text-ink focus:border-ink focus:outline-none"
                />
                {draft.discount_type === "percent" && (
                  <span className="font-sans text-[15px] text-ink">%</span>
                )}
              </div>
            )}
          </div>
          {draft.discount_type === "tiered" && (
            <textarea
              rows={4}
              value={draft.discount_tiers}
              onChange={(e) =>
                setDraft({ ...draft, discount_tiers: e.target.value })
              }
              placeholder={"10000:500\n20000:1000\n29999.99:1500\n*:2000"}
              className={`mt-2 w-full font-mono ${inputClass}`}
            />
          )}
          {draft.discount_type === "tiered" && (
            <p className="mt-1 font-sans text-[13px] text-ink/60">
              One tier per line: <code>price up to:$ off</code>. First tier the
              price fits wins; <code>*</code> means "and above".
            </p>
          )}
        </Field>

        {draft.discount_type && (
          <Field label="Discount applies to">
            <select
              value={draft.discount_scope}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  discount_scope: e.target.value as DiscountScope,
                })
              }
              className={inputClass}
            >
              <option value="order">The whole order</option>
              <option value="machine">The machine / retrofit only</option>
            </select>
          </Field>
        )}

        <label className="mb-4 flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
          />
          <span className="font-sans text-[14px] text-ink">
            Active (reps can pick it)
          </span>
        </label>

        {/* Auto-apply: the promo matches carts on its own (no code) and shows a
            banner. Reveals dates + triggers + the two messages. */}
        <div className="mb-4 border-t border-line pt-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.auto_apply}
              onChange={(e) =>
                setDraft({ ...draft, auto_apply: e.target.checked })
              }
            />
            <span className="font-sans text-[14px] font-semibold text-ink">
              Auto-apply to matching carts (no code needed)
            </span>
          </label>

          {draft.auto_apply && (
            <div className="mt-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start date (Central)">
                  <input
                    type="date"
                    value={draft.starts_on}
                    onChange={(e) =>
                      setDraft({ ...draft, starts_on: e.target.value })
                    }
                    className={inputClass}
                  />
                </Field>
                <Field label="End date (Central)">
                  <input
                    type="date"
                    value={draft.ends_on}
                    onChange={(e) =>
                      setDraft({ ...draft, ends_on: e.target.value })
                    }
                    className={inputClass}
                  />
                </Field>
              </div>

              <Field label="Applies when the machine is…">
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {KIND_OPTIONS.map((k) => (
                    <label key={k.value} className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={draft.trigger_kinds.includes(k.value)}
                        onChange={() =>
                          setDraft({
                            ...draft,
                            trigger_kinds: toggleIn(draft.trigger_kinds, k.value),
                          })
                        }
                      />
                      <span className="font-sans text-[14px] text-ink">
                        {k.label}
                      </span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="…or of family (optional)">
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {FAMILY_OPTIONS.map((f) => (
                    <label key={f} className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={draft.trigger_families.includes(f)}
                        onChange={() =>
                          setDraft({
                            ...draft,
                            trigger_families: toggleIn(draft.trigger_families, f),
                          })
                        }
                      />
                      <span className="font-sans text-[14px] capitalize text-ink">
                        {f}
                      </span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="…or specific product IDs (optional, comma-separated)">
                <input
                  value={draft.trigger_product_ids}
                  onChange={(e) =>
                    setDraft({ ...draft, trigger_product_ids: e.target.value })
                  }
                  placeholder="e.g. 1352372845, 1352372846"
                  className={inputClass}
                />
              </Field>

              <Field label="Nudge message — before qualifying (use {countdown})">
                <textarea
                  value={draft.nudge_message}
                  rows={2}
                  onChange={(e) =>
                    setDraft({ ...draft, nudge_message: e.target.value })
                  }
                  placeholder="Active promo — expires in {countdown}. Add a machine to see your discount."
                  className={inputClass}
                />
              </Field>

              <Field label="Applied message — once qualifying (use {savings})">
                <textarea
                  value={draft.applied_message}
                  rows={2}
                  onChange={(e) =>
                    setDraft({ ...draft, applied_message: e.target.value })
                  }
                  placeholder="Promotion applied — you save {savings}!"
                  className={inputClass}
                />
              </Field>
            </div>
          )}
        </div>

        {error && (
          <p className="mb-3 font-sans text-[14px] text-red-700">{error}</p>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving || !draft.code.trim()}
            className="bg-ink px-4 py-2.5 font-display text-[14px] font-extrabold uppercase tracking-wide text-white transition hover:bg-graphite disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Add promo"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={() => setDraft(EMPTY)}
              className="px-4 py-2.5 font-sans text-[14px] font-semibold text-ink hover:underline"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </FormModal>
  );
}

function describe(p: PromoFull): string {
  const parts: string[] = [];
  if (p.discount_type === "percent") parts.push(`${p.discount_value}% off`);
  else if (p.discount_type === "fixed") parts.push(`$${p.discount_value} off`);
  else if (p.discount_type === "tiered") parts.push("tiered $ off");
  if (p.discount_type && p.discount_scope === "machine") parts.push("machine only");
  if (p.auto_apply) {
    const window =
      p.starts_on && p.ends_on ? ` ${p.starts_on}→${p.ends_on}` : "";
    parts.push(`auto-apply${window}`);
  }
  if (p.template_id) parts.push("custom template");
  return parts.length ? `— ${parts.join(", ")}` : "— no discount";
}
